import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Chip, IconButton, Stack,
  CircularProgress, LinearProgress, Alert, Button, Divider,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  CheckCircle as CheckIcon, Extension as ExtensionIcon,
  CloudDone as CloudDoneIcon, Shield as ShieldIcon,
  Settings as SettingsIcon, MoreVert as MoreIcon,
  Refresh as RefreshIcon, PlayArrow as PlayIcon,
  ErrorOutline as ErrorIcon, Bolt as BoltIcon,
} from '@mui/icons-material';

/**
 * Ext Admin — First run (rich variant)
 *
 * Staff / developer-facing authoring-extension first-run. The EQUIVALENT
 * end-user popup first-run state is simpler (just "Connected" pill inside
 * the 300x200 popup) and lives in `ext-v5/ExtV5PopupMockup.js`.
 *
 * Three states via toggle:
 *  1. Connecting to Automatrix (spinner)
 *  2. Bundle fetched (confirmation card)
 *  3. Ready (call to action)
 */

const SIDEBAR_WIDTH = 400;
const PURPLE = '#7b1fa2';

const ExtAdminFirstRunMockup = () => {
  const [stage, setStage] = useState('connecting');
  const [connectProgress, setConnectProgress] = useState(18);

  useEffect(() => {
    if (stage !== 'connecting') return;
    const t = setInterval(() => {
      setConnectProgress(p => {
        const next = p + 6 + Math.random() * 8;
        return next >= 100 ? 95 : next;
      });
    }, 600);
    return () => clearInterval(t);
  }, [stage]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', bgcolor: '#eef1f5', p: 3, minHeight: 'calc(100vh - 180px)' }}>
      {/* Stage picker (mockup control, not part of the real UI) */}
      <ToggleButtonGroup
        value={stage}
        exclusive
        onChange={(_, v) => v && setStage(v)}
        size="small"
        sx={{ mb: 2, bgcolor: '#fff' }}
      >
        <ToggleButton value="connecting" sx={{ textTransform: 'none', fontSize: '0.75rem', px: 2 }}>
          1. Connecting
        </ToggleButton>
        <ToggleButton value="fetched" sx={{ textTransform: 'none', fontSize: '0.75rem', px: 2 }}>
          2. Fetched
        </ToggleButton>
        <ToggleButton value="ready" sx={{ textTransform: 'none', fontSize: '0.75rem', px: 2 }}>
          3. Ready
        </ToggleButton>
        <ToggleButton value="unauth" sx={{ textTransform: 'none', fontSize: '0.75rem', px: 2 }}>
          (offline)
        </ToggleButton>
      </ToggleButtonGroup>

      <Paper elevation={6} sx={{
        width: SIDEBAR_WIDTH, display: 'flex', flexDirection: 'column',
        bgcolor: '#fff', overflow: 'hidden',
        border: '1px solid #d5d9df', minHeight: 560,
      }}>
        {/* Toolbar */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1,
          bgcolor: PURPLE, color: '#fff',
        }}>
          <ExtensionIcon sx={{ fontSize: 20 }} />
          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Automatrix</Typography>
          <Chip label="v5.0.0" size="small" sx={{
            height: 18, fontSize: '0.65rem', bgcolor: 'rgba(255,255,255,0.18)',
            color: '#fff', fontWeight: 600,
          }} />
          <Chip label="FIRST RUN" size="small" sx={{
            height: 18, fontSize: '0.62rem', bgcolor: '#ffb300',
            color: '#3e2723', fontWeight: 700,
          }} />
          <Box sx={{ flexGrow: 1 }} />
          <IconButton size="small" sx={{ color: '#fff' }}>
            <SettingsIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton size="small" sx={{ color: '#fff' }}>
            <MoreIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {/* Content varies by stage */}
        <Box sx={{
          flexGrow: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', p: 3, gap: 2,
        }}>
          {stage === 'connecting' && (
            <>
              <CircularProgress sx={{ color: PURPLE }} size={48} thickness={4} />
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#263238', textAlign: 'center' }}>
                Connecting to Automatrix…
              </Typography>
              <Stack spacing={0.5} alignItems="center" sx={{ maxWidth: 320 }}>
                <Typography variant="caption" sx={{ fontSize: '0.72rem', color: '#607d8b', textAlign: 'center' }}>
                  Fetching <span style={{ fontFamily: 'monospace' }}>/extension/manifest</span> · cache is empty
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.68rem', color: '#90a4ae', textAlign: 'center' }}>
                  This happens once after install. Future loads use the local cache.
                </Typography>
              </Stack>
              <Box sx={{ width: '80%' }}>
                <LinearProgress
                  variant="determinate" value={connectProgress}
                  sx={{
                    height: 5, borderRadius: 2, bgcolor: '#ede7f6',
                    '& .MuiLinearProgress-bar': { bgcolor: PURPLE },
                  }}
                />
                <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
                  <Typography variant="caption" sx={{ fontSize: '0.62rem', color: '#9575cd' }}>
                    {connectProgress < 40 ? 'manifest'
                      : connectProgress < 75 ? 'scripts bundle'
                      : 'filter rules'}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.62rem', color: '#9575cd', fontFamily: 'monospace' }}>
                    {Math.round(connectProgress)}%
                  </Typography>
                </Stack>
              </Box>
              <Button
                size="small" variant="text"
                sx={{ mt: 1, textTransform: 'none', fontSize: '0.7rem', color: '#607d8b' }}
                onClick={() => setStage('fetched')}
              >
                Skip to next stage
              </Button>
            </>
          )}

          {stage === 'fetched' && (
            <Box sx={{ width: '100%' }}>
              <Stack alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <CheckIcon sx={{ color: '#43a047', fontSize: 42 }} />
                <Typography sx={{ fontWeight: 700, fontSize: '1rem', textAlign: 'center', color: '#1b5e20' }}>
                  Connected to Automatrix
                </Typography>
              </Stack>

              <Paper elevation={0} sx={{
                bgcolor: '#e8f5e9', border: '1px solid #a5d6a7',
                borderRadius: 1.5, p: 1.5, mb: 1.5,
              }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                  <CloudDoneIcon sx={{ color: '#2e7d32', fontSize: 18 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#1b5e20' }}>
                    Scripts fetched
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ fontSize: '0.85rem', color: '#2e7d32', fontWeight: 600 }}>
                  46 scripts across 16 apps
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#558b2f', fontFamily: 'monospace' }}>
                  etag W/"abc123" · 118 KB · cached 12:04:18
                </Typography>
              </Paper>

              <Paper elevation={0} sx={{
                bgcolor: '#e3f2fd', border: '1px solid #90caf9',
                borderRadius: 1.5, p: 1.5, mb: 1.5,
              }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                  <ShieldIcon sx={{ color: '#1565c0', fontSize: 18 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#0d47a1' }}>
                    Filter rules applied
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ fontSize: '0.85rem', color: '#1565c0', fontWeight: 600 }}>
                  184 rules in DNR
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#1976d2', fontFamily: 'monospace' }}>
                  3 static · 181 dynamic · 0 session · regex 12/1000
                </Typography>
              </Paper>

              <Paper elevation={0} sx={{
                bgcolor: '#ede7f6', border: '1px solid #b39ddb',
                borderRadius: 1.5, p: 1.5, mb: 1,
              }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                  <BoltIcon sx={{ color: PURPLE, fontSize: 16 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#4a148c' }}>
                    Live updates enabled
                  </Typography>
                </Stack>
                <Typography variant="caption" sx={{ fontSize: '0.68rem', color: '#6a1b9a' }}>
                  WebSocket connected · script edits appear in under 2 seconds
                </Typography>
              </Paper>

              <Button
                fullWidth variant="contained"
                sx={{
                  mt: 1, textTransform: 'none', bgcolor: PURPLE,
                  '&:hover': { bgcolor: '#6a1b9a' },
                }}
                onClick={() => setStage('ready')}
              >
                Continue →
              </Button>
            </Box>
          )}

          {stage === 'ready' && (
            <Stack alignItems="center" spacing={1.5} sx={{ width: '100%' }}>
              <Box sx={{
                width: 56, height: 56, borderRadius: '50%',
                background: `linear-gradient(135deg, ${PURPLE} 0%, #4a148c 100%)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(123, 31, 162, 0.35)',
              }}>
                <CheckIcon sx={{ color: '#fff', fontSize: 32 }} />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', textAlign: 'center' }}>
                Ready
              </Typography>
              <Typography variant="body2" sx={{
                fontSize: '0.82rem', color: '#546e7a', textAlign: 'center', maxWidth: 300,
              }}>
                You can launch automations from the Automatrix dashboard, or pick a script below to run manually.
              </Typography>

              <Divider sx={{ width: '100%', my: 1 }} />

              <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
                <Chip
                  icon={<CloudDoneIcon sx={{ fontSize: 14 }} />}
                  label="46 scripts"
                  size="small"
                  sx={{ flexGrow: 1, bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '0.7rem' }}
                />
                <Chip
                  icon={<ShieldIcon sx={{ fontSize: 14 }} />}
                  label="184 rules"
                  size="small"
                  sx={{ flexGrow: 1, bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600, fontSize: '0.7rem' }}
                />
                <Chip
                  icon={<BoltIcon sx={{ fontSize: 14 }} />}
                  label="WS live"
                  size="small"
                  sx={{ flexGrow: 1, bgcolor: '#ede7f6', color: '#4a148c', fontWeight: 600, fontSize: '0.7rem' }}
                />
              </Stack>

              <Button
                fullWidth variant="contained" startIcon={<PlayIcon />}
                sx={{
                  mt: 1, textTransform: 'none', fontWeight: 700,
                  bgcolor: PURPLE, '&:hover': { bgcolor: '#6a1b9a' },
                }}
              >
                Pick a script
              </Button>
              <Button
                fullWidth variant="outlined" size="small"
                sx={{ textTransform: 'none', color: PURPLE, borderColor: PURPLE }}
              >
                Open dashboard
              </Button>
            </Stack>
          )}

          {stage === 'unauth' && (
            <Stack alignItems="center" spacing={1.5} sx={{ width: '100%' }}>
              <ErrorIcon sx={{ color: '#e53935', fontSize: 48 }} />
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#c62828', textAlign: 'center' }}>
                Not signed in
              </Typography>
              <Alert severity="warning" sx={{ width: '100%', fontSize: '0.78rem' }}>
                No auth token in <code>chrome.storage.local</code>. Visit{' '}
                <b>automatrix.nucleos.com</b> and sign in — the frontend bridge will forward your token automatically.
              </Alert>
              <Typography variant="caption" sx={{ fontSize: '0.68rem', color: '#90a4ae', textAlign: 'center' }}>
                With no cache and no token, no scripts are available.
              </Typography>
              <Button
                fullWidth variant="contained" startIcon={<RefreshIcon />}
                onClick={() => setStage('connecting')}
                sx={{
                  mt: 1, textTransform: 'none', bgcolor: PURPLE,
                  '&:hover': { bgcolor: '#6a1b9a' },
                }}
              >
                Retry connection
              </Button>
            </Stack>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75,
          borderTop: '1px solid #eceff2', bgcolor: '#f7f9fb',
        }}>
          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#90a4ae', fontFamily: 'monospace' }}>
            api: automatrix.nucleos.com
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#90a4ae' }}>
            {stage === 'connecting' ? 'fetching…' :
             stage === 'fetched' ? 'cache primed' :
             stage === 'ready' ? 'ready' : 'offline'}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default ExtAdminFirstRunMockup;
